// App.tsx
import React, { useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom'; // 🔥 Import useLocation
import { ThemeProvider } from 'react-jss';
import { ReactNotifications } from 'react-notifications-component';
import { useFullscreen } from 'react-use';
import { TourProvider } from '@reactour/tour';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ToastContainer } from 'react-toastify';
import ThemeContext from '../contexts/themeContext';
import Wrapper from '../layout/Wrapper/Wrapper';
import Portal from '../layout/Portal/Portal';
import useDarkMode from '../hooks/useDarkMode';
import COLORS from '../common/data/enumColors';
import { getOS } from '../helpers/helpers';
import steps, { styles } from '../steps';
import AsideRoutes from '../layout/Aside/AsideRoutes';
import { ToastCloseButton } from '../components/bootstrap/Toasts';
import CitySelector from '../components/citySelector/CitySelector';
import { useCity } from '../contexts/cityContexts';

const App = () => {
	getOS();

	const location = useLocation(); // 🔥 Get current location

	dayjs.extend(localizedFormat);
	dayjs.extend(relativeTime);

	const { themeStatus, darkModeStatus } = useDarkMode();
	const { isSuperAdmin, currentCity } = useCity();

	const theme = {
		theme: themeStatus,
		primary: COLORS.PRIMARY.code,
		secondary: COLORS.SECONDARY.code,
		success: COLORS.SUCCESS.code,
		info: COLORS.INFO.code,
		warning: COLORS.WARNING.code,
		danger: COLORS.DANGER.code,
		dark: COLORS.DARK.code,
		light: COLORS.LIGHT.code,
	};

	useEffect(() => {
		if (darkModeStatus) {
			document.documentElement.setAttribute('theme', 'dark');
			document.documentElement.setAttribute('data-bs-theme', 'dark');
		}
		return () => {
			document.documentElement.removeAttribute('theme');
			document.documentElement.removeAttribute('data-bs-theme');
		};
	}, [darkModeStatus]);

	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
	const ref = useRef(null);
	// @ts-ignore
	useFullscreen(ref, fullScreenStatus, {
		onClose: () => setFullScreenStatus(false),
	});

	useLayoutEffect(() => {
		if (process.env.REACT_APP_MODERN_DESGIN === 'true') {
			document.body.classList.add('modern-design');
		} else {
			document.body.classList.remove('modern-design');
		}
	}, []);

	// 🔥 Check if current page is login page
	const isLoginPage = location.pathname === '/login' || location.pathname === '/';

	return (
		<ThemeProvider theme={theme}>
			<TourProvider steps={steps} styles={styles} showNavigation={false} showBadge={false}>
				<div
					ref={ref}
					className='app'
					style={{
						backgroundColor: fullScreenStatus ? 'var(--bs-body-bg)' : undefined,
						zIndex: fullScreenStatus ? 1 : undefined,
						overflow: fullScreenStatus ? 'scroll' : undefined,
					}}>
					{/* 🔥 ONLY show city selector if NOT on login page */}
					{!isLoginPage && isSuperAdmin && (
						<div
							className='app-header-city-selector'
							style={{
								padding: '10px 20px',
								borderBottom: '1px solid var(--bs-border-color)',
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								backgroundColor: 'var(--bs-body-bg)',
							}}>
							<div>
								<strong>City Management</strong>
							</div>
							<CitySelector showLabel={false} placeholder='Select a city' />
						</div>
					)}

					{/* 🔥 Show city name for non-admin users, but NOT on login page */}
					{!isLoginPage && !isSuperAdmin && currentCity && (
						<div
							className='app-header-city-display'
							style={{
								padding: '10px 20px',
								borderBottom: '1px solid var(--bs-border-color)',
								display: 'flex',
								justifyContent: 'flex-end',
								alignItems: 'center',
								backgroundColor: 'var(--bs-body-bg)',
							}}>
							<span className='font-medium'>City: </span>
							<span className='ml-1 text-blue-600 font-semibold'>
								{currentCity.label}
							</span>
						</div>
					)}

					<AsideRoutes />
					<Wrapper />
				</div>
				<Portal id='portal-notification'>
					<ReactNotifications />
				</Portal>
				<ToastContainer closeButton={ToastCloseButton} toastClassName='toast show' />
			</TourProvider>
		</ThemeProvider>
	);
};

export default App;
